import { useState } from 'react';
import { Checkbox, Typography, theme as antdTheme } from 'antd';
import { space } from '@viq-admin/ui';
import type { ExportColumnDescriptor } from './types';

interface Props {
  availableColumns: ExportColumnDescriptor[];
  hiddenColumns: string[];
  onHiddenColumnsChange: (hidden: string[]) => void;
  /** False for a stakeholder, or a conference with no questions. */
  showDiscovery: boolean;
  includeDiscovery: boolean;
  onIncludeDiscoveryChange: (v: boolean) => void;
  includeDiscoverySummary: boolean;
  onIncludeDiscoverySummaryChange: (v: boolean) => void;
}

/**
 * Collapsible column selection for the export dialog.
 *
 * Columns come from the server's catalog — this component never holds its own
 * copy of the header list. A hardcoded copy is how apps/portal's csvExport.ts
 * ended up dead in this repo with 18 columns contradicting the server's 19.
 *
 * The summary count is of FIXED columns only. Discovery column counts vary per
 * conference, so counting them would change the label between events for
 * reasons unrelated to anything the user chose.
 */
export function ColumnPicker({
  availableColumns,
  hiddenColumns,
  onHiddenColumnsChange,
  showDiscovery,
  includeDiscovery,
  onIncludeDiscoveryChange,
  includeDiscoverySummary,
  onIncludeDiscoverySummaryChange,
}: Props) {
  const { token } = antdTheme.useToken();
  const [open, setOpen] = useState(false);

  const total = availableColumns.length;
  const kept = total - hiddenColumns.length;
  const untouched = hiddenColumns.length === 0 && includeDiscovery && includeDiscoverySummary;
  const label = untouched ? 'All columns' : `${kept} of ${total} columns`;

  function toggle(key: string, checked: boolean) {
    onHiddenColumnsChange(
      checked ? hiddenColumns.filter((k) => k !== key) : [...hiddenColumns, key],
    );
  }

  return (
    <div>
      <Typography.Text strong>Columns</Typography.Text>
      <div
        style={{
          border: `1px solid ${token.colorBorder}`,
          borderRadius: 6,
          padding: space.sm,
          marginTop: space.sm,
        }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v); }}
          style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
        >
          <strong>{label}</strong>
          <Typography.Text type="secondary">{open ? 'Hide' : 'Choose columns'}</Typography.Text>
        </div>

        {open && (
          <div style={{ marginTop: space.sm }}>
            <div style={{ marginBottom: space.xs }}>
              <Typography.Link onClick={() => onHiddenColumnsChange([])}>Select all</Typography.Link>
              {' · '}
              <Typography.Link
                onClick={() =>
                  // Never produce an empty export: the first column stays.
                  onHiddenColumnsChange(availableColumns.slice(1).map((c) => c.key))
                }
              >
                Select none
              </Typography.Link>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.xs }}>
              {availableColumns.map((col) => (
                <Checkbox
                  key={col.key}
                  aria-label={col.header}
                  checked={!hiddenColumns.includes(col.key)}
                  onChange={(e) => toggle(col.key, e.target.checked)}
                >
                  {col.header}
                </Checkbox>
              ))}
            </div>

            {showDiscovery && (
              <div style={{ marginTop: space.sm, borderTop: `1px solid ${token.colorSplit}`, paddingTop: space.sm }}>
                <Checkbox
                  aria-label="Discovery answers"
                  checked={includeDiscovery}
                  onChange={(e) => onIncludeDiscoveryChange(e.target.checked)}
                >
                  Discovery answers{' '}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    one column per question
                  </Typography.Text>
                </Checkbox>
                <br />
                <Checkbox
                  aria-label="Discovery Summary"
                  checked={includeDiscoverySummary}
                  onChange={(e) => onIncludeDiscoverySummaryChange(e.target.checked)}
                >
                  Discovery Summary{' '}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    all answers in one cell
                  </Typography.Text>
                </Checkbox>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
